import { motion, AnimatePresence } from 'framer-motion';
import { useState, useRef, useEffect } from 'react';

const ScratchCardScreen = () => {
    const [activeTab, setActiveTab] = useState('unopened');
    const [scratchCards, setScratchCards] = useState([
        {
            id: 1,
            isOpened: false,
            isScratched: false,
            value: null,
            expireDate: new Date().toISOString().split('T')[0], // Today
            title: 'Weekly Bonus',
            scratchDate: null
        },
        {
            id: 2,
            isOpened: false,
            isScratched: false,
            value: null,
            expireDate: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().split('T')[0], // Tomorrow
            title: 'Christmas Special',
            scratchDate: null
        },
        {
            id: 3,
            isOpened: false,
            isScratched: false,
            value: null,
            expireDate: '2024-12-28',
            title: 'Weekend Reward',
            scratchDate: null
        },
        {
            id: 4,
            isOpened: true,
            isScratched: true,
            value: '$25',
            expireDate: '2024-12-20',
            title: 'Daily Bonus',
            scratchDate: '2024-12-18'
        },
        {
            id: 5,
            isOpened: true,
            isScratched: true,
            value: '$10',
            expireDate: '2024-12-22',
            title: 'Welcome Gift',
            scratchDate: '2024-12-15'
        },
    ]);

    const [selectedCard, setSelectedCard] = useState(null);

    const openScratchCard = (card) => {
        if (!card.isOpened) {
            setSelectedCard({ ...card });
        }
    };

    const closeScratchCard = () => {
        if (selectedCard && selectedCard.isScratched) {
            setScratchCards(prev =>
                prev.map(card =>
                    card.id === selectedCard.id
                        ? {
                            ...card,
                            isOpened: true,
                            value: selectedCard.value,
                            scratchDate: new Date().toISOString().split('T')[0] // Add scratch date
                        }
                        : card
                )
            );
        }
        setSelectedCard(null);
    };

    const unopenedCards = scratchCards.filter(card => !card.isOpened);
    const openedCards = scratchCards.filter(card => card.isOpened);

    const getExpireText = (dateString) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const expireDate = new Date(dateString);
        expireDate.setHours(0, 0, 0, 0);

        const diffTime = expireDate - today;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays === 0) {
            return 'Expiring today';
        } else if (diffDays === 1) {
            return 'Expiring tomorrow';
        } else {
            return `Expires: ${expireDate.toLocaleDateString('en-US', {
                day: 'numeric',
                month: 'short',
                year: 'numeric'
            })}`;
        }
    };

    // Hide scrollbar when components are animating
    useEffect(() => {
        const style = document.createElement('style');
        style.innerHTML = `
            body {
                overflow-y: scroll;
                scrollbar-width: none;
                -ms-overflow-style: none;
            }
            body::-webkit-scrollbar {
                display: none;
            }
        `;
        document.head.appendChild(style);

        return () => {
            document.head.removeChild(style);
        };
    }, []);

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="bg-light"
            style={{
                backgroundColor: '#f8f9fa',
                minHeight: '100vh'
            }}
        >
            {/* Remove container padding-top to eliminate top gap */}
            <div className="container pt-0">
                {/* Header - Reduced margin */}
                <motion.div
                    initial={{ y: -30, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.2 }}
                    className="text-center mb-3 pt-3" // Added pt-3 for minimal top padding
                >
                    <h1 className="h3 fw-bold text-primary mb-2">
                        Scratch Card Rewards
                    </h1>
                    <p className="text-muted small mb-0">
                        Scratch your cards to reveal amazing prizes!
                    </p>
                </motion.div>

                {/* Tabs Navigation - Horizontal Layout */}
                <div className="d-flex justify-content-center mb-3">
                    <div className="card shadow-sm" style={{ minWidth: '350px' }}>
                        <div className="card-body p-0 d-flex">
                            <button
                                className={`btn btn-sm flex-fill ${activeTab === 'unopened' ? 'btn-primary' : 'btn-outline-primary'} rounded-0`}
                                onClick={() => setActiveTab('unopened')}
                                style={{
                                    fontWeight: '600',
                                    padding: '10px 0',
                                    border: 'none',
                                    fontSize: '0.9rem'
                                }}
                            >
                                <i className="bi bi-gift me-1"></i>
                                Un-opened
                                <span className="badge bg-light text-primary ms-1">{unopenedCards.length}</span>
                            </button>
                            <button
                                className={`btn btn-sm flex-fill ${activeTab === 'opened' ? 'btn-success' : 'btn-outline-success'} rounded-0`}
                                onClick={() => setActiveTab('opened')}
                                style={{
                                    fontWeight: '600',
                                    padding: '10px 0',
                                    border: 'none',
                                    fontSize: '0.9rem'
                                }}
                            >
                                <i className="bi bi-check-circle me-1"></i>
                                Opened
                                <span className="badge bg-light text-success ms-1">{openedCards.length}</span>
                            </button>
                        </div>
                    </div>
                </div>

                {/* Cards Grid - Always 2 items per row */}
                <div className="row">
                    <div className="col-12">
                        <AnimatePresence mode="wait">
                            {activeTab === 'unopened' && (
                                <motion.div
                                    key="unopened"
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: 20 }}
                                    transition={{ duration: 0.3 }}
                                >
                                    {unopenedCards.length > 0 ? (
                                        <div className="row g-3">
                                            {unopenedCards.map((card, index) => (
                                                <div key={card.id} className="col-6">
                                                    <ScratchCardItem
                                                        card={card}
                                                        index={index}
                                                        onOpen={openScratchCard}
                                                        getExpireText={getExpireText}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="text-center py-4">
                                            <div className="h1 text-muted mb-2">🎁</div>
                                            <h5 className="text-muted">No un-opened cards</h5>
                                            <p className="text-muted small">Come back later for new cards!</p>
                                        </div>
                                    )}
                                </motion.div>
                            )}

                            {activeTab === 'opened' && (
                                <motion.div
                                    key="opened"
                                    initial={{ opacity: 0, x: 20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: -20 }}
                                    transition={{ duration: 0.3 }}
                                >
                                    {openedCards.length > 0 ? (
                                        <div className="row g-3">
                                            {openedCards.map((card, index) => (
                                                <div key={card.id} className="col-6">
                                                    <OpenedCardItem
                                                        card={card}
                                                        index={index}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="text-center py-4">
                                            <div className="h1 text-muted mb-2">📭</div>
                                            <h5 className="text-muted">No opened cards</h5>
                                            <p className="text-muted small">Start scratching to see rewards!</p>
                                        </div>
                                    )}
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                </div>
            </div>

            {/* Scratch Card Modal */}
            <AnimatePresence>
                {selectedCard && (
                    <ScratchCardModal
                        card={selectedCard}
                        onClose={closeScratchCard}
                        onScratchComplete={(value) => {
                            setSelectedCard(prev => ({ ...prev, isScratched: true, value }));
                        }}
                    />
                )}
            </AnimatePresence>

            {/* Bootstrap Icons CSS */}
            <link
                href="https://cdn.jsdelivr.net/npm/bootstrap-icons@1.10.0/font/bootstrap-icons.css"
                rel="stylesheet"
            />
        </motion.div>
    );
};

const ScratchCardItem = ({ card, index, onOpen, getExpireText }) => {
    return (
        <motion.div
            initial={{ scale: 0.8, opacity: 0, y: 50 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            transition={{
                delay: index * 0.1,
                type: "spring",
                stiffness: 100
            }}
            whileHover={{
                scale: 1.03,
                y: -3,
                transition: { type: "spring", stiffness: 300 }
            }}
            whileTap={{ scale: 0.97 }}
            className="card h-100 shadow-sm border-0 cursor-pointer"
            style={{ cursor: 'pointer' }}
            onClick={() => onOpen(card)}
        >
            <div className="card-body p-3 d-flex flex-column text-center position-relative">
                {/* Expiration Date Badge */}
                <div className="position-absolute top-0 start-50 translate-middle mt-1">
                    <span className="badge bg-warning text-dark px-2 py-1 small">
                        <i className="bi bi-clock me-1"></i>
                        {getExpireText(card.expireDate)}
                    </span>
                </div>

                {/* Card Icon */}
                <motion.div
                    animate={{
                        scale: [1, 1.05, 1],
                        rotate: [0, 3, -3, 0]
                    }}
                    transition={{
                        duration: 3,
                        repeat: Infinity,
                        repeatType: "reverse",
                    }}
                    className="h2 text-warning mb-2 mt-3"
                >
                    🎁
                </motion.div>

                {/* Card Title */}
                <h6 className="card-title fw-bold text-dark mb-1 small">{card.title}</h6>

                {/* Action Button - Smaller font to prevent line break */}
                <motion.div
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="mt-auto"
                >
                    <button
                        className="btn btn-primary w-100 py-1"
                        style={{ fontSize: '0.7rem' }} // Reduced font size
                    >
                        <i className="bi bi-hand-index me-1"></i>
                        Scratch Now
                    </button>
                </motion.div>
            </div>

            {/* Shimmer Effect */}
            <motion.div
                animate={{
                    x: [-100, 300, -100],
                }}
                transition={{
                    duration: 3,
                    repeat: Infinity,
                    repeatType: "loop",
                }}
                className="position-absolute top-0 start-0 w-100 h-100"
                style={{
                    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)',
                    transform: 'skewX(-20deg)',
                    pointerEvents: 'none'
                }}
            />
        </motion.div>
    );
};

const OpenedCardItem = ({ card, index }) => {
    const isWinner = card.value && !card.value.includes('Try Again');

    // Format scratch date for display
    const formatScratchDate = (dateString) => {
        if (!dateString) return 'Unknown date';

        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
        });
    };

    return (
        <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{
                delay: index * 0.1,
                type: "spring",
                stiffness: 100
            }}
            className="card h-100 shadow-sm border-0"
        >
            <div className={`card-body p-3 d-flex flex-column text-center ${isWinner ? 'bg-success bg-opacity-10' : 'bg-light'}`}>
                {/* Status Badge */}
                <div className="position-absolute top-0 start-50 translate-middle mt-1">
                    <span className={`badge ${isWinner ? 'bg-success' : 'bg-secondary'} px-2 py-1 small`}>
                        <i className={`bi ${isWinner ? 'bi-trophy' : 'bi-info-circle'} me-1`}></i>
                        {isWinner ? 'Winner!' : 'Completed'}
                    </span>
                </div>

                {/* Card Icon */}
                <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.2, type: "spring" }}
                    className={`h2 mb-2 mt-3 ${isWinner ? 'text-success' : 'text-secondary'}`}
                >
                    {isWinner ? '🎉' : '📭'}
                </motion.div>

                {/* Card Title */}
                <h6 className="card-title fw-bold text-dark mb-1 small">{card.title}</h6>

                {/* Prize Value */}
                <div className="my-2">
                    <h5 className={`fw-bold ${isWinner ? 'text-success' : 'text-secondary'}`}>
                        {card.value}
                    </h5>
                    <small className="text-muted">Prize Value</small>
                </div>

                {/* Scratch Date - Replaced expiration date */}
                <div className="mt-auto">
                    <small className="text-muted">
                        <i className="bi bi-calendar-check me-1"></i>
                        Scratched: {formatScratchDate(card.scratchDate)}
                    </small>
                </div>
            </div>
        </motion.div>
    );
};

const ScratchCardModal = ({ card, onClose, onScratchComplete }) => {
    const canvasRef = useRef(null);
    const containerRef = useRef(null);
    const modalRef = useRef(null);
    const [isScratching, setIsScratching] = useState(false);
    const [prizeRevealed, setPrizeRevealed] = useState(false);
    const [prizeValue, setPrizeValue] = useState(null);
    const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });
    const [autoRevealTriggered, setAutoRevealTriggered] = useState(false);

    // Generate prize value once when component mounts
    useEffect(() => {
        const generatedPrize = generatePrizeValue();
        setPrizeValue(generatedPrize);
    }, []);

    // Set up canvas size and drawing
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const updateCanvasSize = () => {
            const containerRect = container.getBoundingClientRect();
            const width = containerRect.width;
            const height = 350;
            setCanvasSize({ width, height });
        };

        updateCanvasSize();
        window.addEventListener('resize', updateCanvasSize);

        return () => {
            window.removeEventListener('resize', updateCanvasSize);
        };
    }, []);

    // Draw when canvas size changes or prize revealed
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas || canvasSize.width === 0) return;

        const ctx = canvas.getContext('2d');

        // Set canvas size
        canvas.width = canvasSize.width;
        canvas.height = canvasSize.height;

        if (prizeRevealed) {
            drawRevealedBackground(ctx, canvasSize.width, canvasSize.height, prizeValue);
        } else {
            drawScratchBackground(ctx, canvasSize.width, canvasSize.height);
            drawScratchLayer(ctx, canvasSize.width, canvasSize.height);
        }
    }, [canvasSize, prizeRevealed, prizeValue]);

    // Function to automatically reveal the entire card
    const autoRevealCard = () => {
        const canvas = canvasRef.current;
        if (!canvas || prizeRevealed) return;

        const ctx = canvas.getContext('2d');

        // Clear the entire canvas to reveal the prize
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        drawRevealedBackground(ctx, canvas.width, canvas.height, prizeValue);

        setPrizeRevealed(true);
        onScratchComplete(prizeValue);
        setAutoRevealTriggered(true);
    };

    const drawScratchBackground = (ctx, width, height) => {
        // Draw professional gradient background
        const gradient = ctx.createLinearGradient(0, 0, width, height);
        gradient.addColorStop(0, '#667eea');
        gradient.addColorStop(1, '#764ba2');
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, width, height);

        // Add subtle pattern
        ctx.fillStyle = 'rgba(255, 255, 255, 0.1)';
        for (let i = 0; i < 15; i++) {
            ctx.beginPath();
            ctx.arc(
                Math.random() * width,
                Math.random() * height,
                Math.random() * 3 + 1,
                0,
                Math.PI * 2
            );
            ctx.fill();
        }

        // Draw professional card design
        const cardWidth = width * 0.8;
        const cardHeight = height * 0.6;
        const cardX = (width - cardWidth) / 2;
        const cardY = (height - cardHeight) / 2;

        // Card background with shadow
        ctx.fillStyle = '#ffffff';
        ctx.shadowColor = 'rgba(0, 0, 0, 0.3)';
        ctx.shadowBlur = 15;
        ctx.shadowOffsetX = 0;
        ctx.shadowOffsetY = 5;
        ctx.fillRect(cardX, cardY, cardWidth, cardHeight);

        // Reset shadow
        ctx.shadowColor = 'transparent';
        ctx.shadowBlur = 0;

        // Card border
        ctx.strokeStyle = '#e0e0e0';
        ctx.lineWidth = 2;
        ctx.strokeRect(cardX, cardY, cardWidth, cardHeight);

        // Card title
        ctx.font = 'bold 24px Arial';
        ctx.fillStyle = '#333333';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(card.title, width / 2, cardY + 40);

        // Question mark icon
        ctx.font = 'bold 48px Arial';
        ctx.fillStyle = '#666666';
        ctx.fillText('❓', width / 2, height / 2);

        // Instruction text
        ctx.font = '14px Arial';
        ctx.fillStyle = '#888888';
        ctx.fillText('Scratch to reveal your prize', width / 2, cardY + cardHeight - 30);
    };

    const drawRevealedBackground = (ctx, width, height, prize) => {
        // Draw celebration gradient background
        const gradient = ctx.createLinearGradient(0, 0, width, height);
        gradient.addColorStop(0, '#4facfe');
        gradient.addColorStop(1, '#00f2fe');
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, width, height);

        // Add celebration particles
        ctx.fillStyle = 'rgba(255, 255, 255, 0.6)';
        for (let i = 0; i < 20; i++) {
            ctx.beginPath();
            ctx.arc(
                Math.random() * width,
                Math.random() * height,
                Math.random() * 4 + 1,
                0,
                Math.PI * 2
            );
            ctx.fill();
        }

        // Celebration emoji - centered
        ctx.font = 'bold 48px Arial';
        ctx.fillStyle = '#FFD700';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('🎉', width / 2, height / 2 - 40);

        // Congratulations text - centered
        ctx.font = 'bold 24px Arial';
        ctx.fillStyle = '#ffffff';
        ctx.fillText('Congratulations!', width / 2, height / 2 - 10);

        // Prize value with emphasis - centered
        ctx.font = 'bold 36px Arial';
        ctx.fillStyle = '#FF6B6B';
        ctx.fillText(prize, width / 2, height / 2 + 30);

        // You won text - centered
        ctx.font = '18px Arial';
        ctx.fillStyle = '#ffffff';
        ctx.fillText('You won:', width / 2, height / 2 + 70);
    };

    const drawScratchLayer = (ctx, width, height) => {
        // Draw metallic scratch layer
        const gradient = ctx.createLinearGradient(0, 0, width, height);
        gradient.addColorStop(0, '#D4AF37');
        gradient.addColorStop(0.3, '#FFD700');
        gradient.addColorStop(0.7, '#D4AF37');
        gradient.addColorStop(1, '#B8860B');
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, width, height);

        // Add metallic texture
        ctx.fillStyle = 'rgba(255, 255, 255, 0.3)';
        for (let i = 0; i < 100; i++) {
            const size = Math.random() * 2 + 1;
            ctx.fillRect(
                Math.random() * width,
                Math.random() * height,
                size, size
            );
        }

        // Add scratch pattern
        ctx.strokeStyle = 'rgba(139, 69, 19, 0.2)';
        ctx.lineWidth = 1;
        for (let i = 0; i < 50; i++) {
            ctx.beginPath();
            ctx.moveTo(Math.random() * width, Math.random() * height);
            ctx.lineTo(Math.random() * width, Math.random() * height);
            ctx.stroke();
        }

        // Professional instruction text
        ctx.fillStyle = '#8B7500';
        ctx.font = 'bold 18px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('SCRATCH TO REVEAL', width / 2, height / 2 - 20);

        ctx.font = '14px Arial';
        ctx.fillText('Drag your finger to scratch', width / 2, height / 2 + 10);

        // Scratch icon
        ctx.font = 'bold 20px Arial';
        ctx.fillText('👆', width / 2, height / 2 + 40);
    };

    const handleScratchStart = (e) => {
        e.preventDefault();
        setIsScratching(true);
        handleScratch(e);
    };

    const handleScratchMove = (e) => {
        if (!isScratching || prizeRevealed) return;
        e.preventDefault();
        handleScratch(e);

        // Check scratch percentage during movement
        checkScratchPercentage();
    };

    const handleScratchEnd = () => {
        if (!isScratching) return;
        setIsScratching(false);

        // Check scratch percentage one more time when user releases
        if (!prizeRevealed && !autoRevealTriggered) {
            checkScratchPercentage();
        }
    };

    const checkScratchPercentage = () => {
        if (prizeRevealed || autoRevealTriggered) return;

        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const pixels = imageData.data;
        let transparentPixels = 0;

        for (let i = 3; i < pixels.length; i += 4) {
            if (pixels[i] === 0) transparentPixels++;
        }

        const percent = (transparentPixels / (pixels.length / 4)) * 100;

        // If 50% or more is scratched, automatically reveal the entire card
        if (percent >= 50) {
            autoRevealCard();
        }
    };

    const handleScratch = (e) => {
        if (prizeRevealed) return;

        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        const rect = canvas.getBoundingClientRect();

        // Get coordinates for both mouse and touch events
        let clientX, clientY;
        if (e.type.includes('touch')) {
            clientX = e.touches[0].clientX;
            clientY = e.touches[0].clientY;
        } else {
            clientX = e.clientX;
            clientY = e.clientY;
        }

        const x = clientX - rect.left;
        const y = clientY - rect.top;

        // Only scratch if coordinates are within canvas bounds
        if (x >= 0 && x <= canvas.width && y >= 0 && y <= canvas.height) {
            // Use destination-out to "scratch" away the top layer
            ctx.globalCompositeOperation = 'destination-out';
            ctx.beginPath();
            ctx.arc(x, y, 25, 0, Math.PI * 2);
            ctx.fill();
        }
    };

    const generatePrizeValue = () => {
        const prizes = [
            { value: '$10', probability: 0.4 },
            { value: '$25', probability: 0.3 },
            { value: '$50', probability: 0.15 },
            { value: '$100', probability: 0.1 },
            { value: 'Try Again', probability: 0.05 },
        ];

        let random = Math.random();
        for (const prize of prizes) {
            random -= prize.probability;
            if (random <= 0) return prize.value;
        }
        return '$10';
    };

    // Prevent scrolling when modal is open
    useEffect(() => {
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, []);

    // Handle click outside modal - do nothing (prevent closing)
    const handleBackdropClick = (e) => {
        // Only close if clicking the exact backdrop (not the modal content)
        if (e.target === modalRef.current) {
            // Do nothing - prevent closing on backdrop click
        }
    };

    return (
        <motion.div
            ref={modalRef}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="modal show d-block"
            style={{
                backgroundColor: 'rgba(0,0,0,0.9)',
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 1050,
                overflow: 'hidden'
            }}
            tabIndex="-1"
            onClick={handleBackdropClick}
        >
            <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
                transition={{ type: "spring", damping: 25 }}
                className="modal-dialog modal-dialog-centered"
                style={{ maxWidth: '450px', margin: '0 auto' }}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="modal-content border-0 shadow-lg" style={{ overflow: 'hidden' }}>
                    <div className="modal-header bg-primary text-white py-2 d-flex justify-content-between align-items-center">
                        <h6 className="modal-title mb-0">
                            <i className="bi bi-gift me-1"></i>
                            {card.title}
                        </h6>
                        {/* Only keep the X button, remove the close button */}
                        <button
                            type="button"
                            className="btn-close btn-close-white"
                            onClick={onClose}
                            style={{ fontSize: '0.7rem' }}
                        ></button>
                    </div>

                    <div className="modal-body p-3 text-center" style={{ touchAction: 'none' }}>
                        {/* Scratch Area Container */}
                        <div
                            ref={containerRef}
                            className="position-relative"
                            style={{
                                touchAction: 'none',
                                userSelect: 'none',
                                WebkitUserSelect: 'none'
                            }}
                        >
                            <canvas
                                ref={canvasRef}
                                className="border rounded shadow-sm w-100"
                                style={{
                                    height: '350px',
                                    cursor: 'crosshair',
                                    touchAction: 'none',
                                    background: 'transparent'
                                }}
                                onMouseDown={handleScratchStart}
                                onMouseMove={handleScratchMove}
                                onMouseUp={handleScratchEnd}
                                onMouseLeave={handleScratchEnd}
                                onTouchStart={handleScratchStart}
                                onTouchMove={handleScratchMove}
                                onTouchEnd={handleScratchEnd}
                                onContextMenu={(e) => e.preventDefault()}
                            />
                        </div>

                        {/* Instructions and Close Button - Simplified */}
                        <div className="mt-3">
                            {prizeRevealed ? (
                                <motion.div
                                    initial={{ y: 20, opacity: 0 }}
                                    animate={{ y: 0, opacity: 1 }}
                                    className="text-center"
                                >
                                    {/* Only show the close button, no additional messages */}
                                    <button
                                        className="btn btn-success btn-sm"
                                        onClick={onClose}
                                    >
                                        <i className="bi bi-check-lg me-1"></i>
                                        Close & Continue
                                    </button>
                                </motion.div>
                            ) : (
                                <div className="d-flex justify-content-between align-items-center">
                                    <small className="text-muted">
                                        <i className="bi bi-info-circle me-1"></i>
                                        Scratch to reveal your prize
                                    </small>
                                    {/* Close button during scratching */}
                                    <button
                                        className="btn btn-outline-secondary btn-sm"
                                        onClick={onClose}
                                    >
                                        <i className="bi bi-x me-1"></i>
                                        Close
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </motion.div>
        </motion.div>
    );
};

export default ScratchCardScreen;